import { JsonRPCService } from '@xengage/gw-portals-transport-js';
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';

export default class PolicyService {
    static createNewPolicyCancellationTransaction(
        policyNumber,
        cancellation,
        additionalHeaders = {}
    ) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'createPolicyCancellationTransaction',
            [policyNumber, cancellation],
            additionalHeaders
        );
    }

    static createNewPolicyChangeTransaction(
        policyChange,
        additionalHeaders = {}
    ) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'createPolicyChangeTransaction',
            [policyChange],
            additionalHeaders
        );
    }

    static getPolicy(policyNumber, additionalHeaders = {}) {
        // Changes has been done to remove homeowners_tfpa data from lobs in backend as it was duplicate of homeowners data. 
        // Filling the homeowners_tfpa before the response has been served to requesting page to avaoid any impact due to edge changes
        return new Promise((resolve, reject)=>{
            JsonRPCService.send(
                getProxiedServiceUrl('gatewayPolicy'),
                'getPolicy',
                [policyNumber],
                additionalHeaders
            ).then((res) => {
                if(res && res.lobs && res.lobs.homeOwners){
                    res.lobs.homeOwners_tfpa = res.lobs.homeOwners
                }

                resolve(res)
            }, (err) => {
                reject(err)
            })
        })
    }

    static getPolicyClaims(policyNumber, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyClaims',
            [policyNumber],
            additionalHeaders
        );
    }

    // US29556 function for policies landing
    static getRecentlyIssuedPolicyCount(producerCode, numberOfDays, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyIssuedPolicyCount',
            [producerCode, numberOfDays],
            additionalHeaders
        );
    }

    static getRecentlyIssuedStatus(producerCode, numberOfDays, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyIssuedStatus',
            [producerCode, numberOfDays],
            additionalHeaders
        );
    }

    static getRecentlyIssuedLOBs(producerCode, numberOfDays, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyIssuedLOBs',
            [producerCode, numberOfDays],
            additionalHeaders
        );
    }

    // US29556 function for policies landing
    static getRecentlyIssuedPolicies_TWIA(
            producerCode, numberOfDays, statusCodes, lobCodes, start, end, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyIssuedPolicies_TWIA',
            [producerCode, numberOfDays, statusCodes, lobCodes, start, end],
            additionalHeaders
        );
    }

    static getRecentlyIssuedPolicies(numberOfDays, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyIssuedPolicies',
            [numberOfDays],
            additionalHeaders
        );
    }

    static getRecentlyViewedPolicies(additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyViewedPolicies',
            [],
            additionalHeaders
        );
    }

    // US29556 function for policies landing
    static getDelinquentPolicyCount(producerCode, statusCodes, productCodes, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getDelinquentPolicyCount',
            [producerCode, statusCodes, productCodes],
            additionalHeaders
        );
    }

    // DE16826
    static getDelinquentPolicies(producerCode, statusCodes, productCodes, start, end, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getDelinquentPolicies',
            [producerCode, statusCodes, productCodes, start, end],
            additionalHeaders
        );
    }

    // DE18898
    static getRecentyIssuedAndDelinquentPolicies(
            producerCode, numberOfDays, billingStatusCodes, statusCodes, 
            lobCodes, start, end, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getRecentyIssuedAndDelinquentPolicies',
            [producerCode, numberOfDays, billingStatusCodes, statusCodes, lobCodes, start, end],
            additionalHeaders
        );
    }

    static getPolicySummariesForCurrentUser(additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getPolicySummariesForCurrentUser',
            [],
            additionalHeaders
        );
    }

    static getPolicyBillingSummaryInfo(policyNumber, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyBillingSummaryInfo',
            [policyNumber],
            additionalHeaders
        );
    }

    static addRecentlyViewedPolicy(policyNumber, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'addRecentlyViewedPolicy',
            [policyNumber],
            additionalHeaders
        );
    }

    static getNotesForPolicy(policyNumber, additionalHeaders) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getNotesForPolicy',
            [policyNumber],
            additionalHeaders
        );
    }

    static getDocumentsForPolicy(policyNumber, additionalHeaders) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getDocumentsForPolicy',
            [policyNumber],
            additionalHeaders
        );
    }

    static createNoteForPolicy(params, additionalHeaders) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'createNoteForPolicy',
            params,
            additionalHeaders
        );
    }

    static createNewPolicyRenewalTransaction(params, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'createPolicyRenewalTransaction',
            params,
            additionalHeaders
        );
    }

    static getPolicyTransactionsForPolicy(
        policyNumber,
        additionalHeaders = {}
    ) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyTransactionsForPolicy',
            [policyNumber],
            additionalHeaders
        );
    }

    static getPolicyPeriodJobType(params, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyPeriodJobType',
            params,
            additionalHeaders
        );
    }

    static getLobData_TWIA(policyPeriodPublicID, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getLobData_TWIA',
            [policyPeriodPublicID],
            additionalHeaders
        );
    }

    static getPolicyJobDetails(policyNumber, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyJobDetails',
            [policyNumber],
            additionalHeaders 
        );
    }
}
